import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API from '../api';
import useGeolocation from '../hooks/useGeolocation';
import { calculateDistance } from '../utils/distance';
import { FaSearch, FaMapMarkerAlt } from 'react-icons/fa'; 
import './LocationListPage.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Hàm helper: Tạo màu gradient ngẫu nhiên (Đồng bộ style với DishRecommendation)
const getPlaceholderStyle = (name) => {
  const gradients = [
    'linear-gradient(135deg, #FF9A9E 0%, #FECFEF 99%, #FECFEF 100%)', // Hồng phấn
    'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)', // Tím mộng mơ
    'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)', // Xanh mint
    'linear-gradient(120deg, #e0c3fc 0%, #8ec5fc 100%)', // Xanh tím
    'linear-gradient(120deg, #f093fb 0%, #f5576c 100%)', // Đỏ hồng
    'linear-gradient(120deg, #f6d365 0%, #fda085 100%)', // Cam vàng
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Tím đậm
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)'  // Hồng đào
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % gradients.length;
  
  return { background: gradients[index] };
};

const LocationListPage = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State tìm kiếm
  const [searchTerm, setSearchTerm] = useState('');

  // Phân trang
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12; 

  const userLocation = useGeolocation();
  const navigate = useNavigate();

  // Hàm tải dữ liệu quán gần đây
  const fetchDefaultLocations = async () => {
    if (!userLocation.loaded || userLocation.error) return;

    try {
      setLoading(true);
      setError(null);
      const { lat, lng } = userLocation.coordinates;
      
      const response = await API.get(
        `/locations/nearby?lat=${lat}&lng=${lng}&radius=5`
      );
      
      // 🔍 DEBUG: Xem dữ liệu API trả về
      console.log("📍 Dữ liệu locations nearby:", response.data.data);
      if (response.data.data.length > 0) {
        console.log("📸 Ảnh của location đầu tiên:", response.data.data[0].images);
      }
      
      if (response.data.success) {
        setLocations(response.data.data);
        setCurrentPage(1);
      } else {
        setError("Không tải được dữ liệu.");
      }
    } catch (err) {
      console.error("Lỗi tải danh sách:", err);
      setError("Lỗi kết nối máy chủ hoặc API bị lỗi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userLocation.loaded) return;

    if (userLocation.error) {
      setLoading(false);
      setError("Không thể xác định vị trí của bạn. Hãy bật GPS và thử lại.");
      return;
    }

    fetchDefaultLocations();
  }, [userLocation.loaded, userLocation.error, userLocation.coordinates]);


  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) {
        fetchDefaultLocations(); 
        return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await API.get(`/locations/search?keyword=${searchTerm}`);
      
      // 🔍 DEBUG
      console.log("🔍 Kết quả search:", response.data.data);
      
      if (response.data.success) {
        setLocations(response.data.data);
        setCurrentPage(1); 
      }
    } catch (err) {
      console.error("Lỗi tìm kiếm:", err);
      setError("Có lỗi xảy ra khi tìm kiếm.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Hàm lấy URL ảnh từ nhiều format khác nhau
  const getImageUrl = (loc) => {
    // Trường hợp 1: Có mảng images
    if (loc.images && Array.isArray(loc.images) && loc.images.length > 0) {
      // Nếu phần tử đầu là object có thuộc tính url
      if (typeof loc.images[0] === 'object' && loc.images[0].url) {
        return loc.images[0].url;
      }
      // Nếu phần tử đầu là string URL trực tiếp
      if (typeof loc.images[0] === 'string') {
        return loc.images[0];
      }
    }
    
    // Trường hợp 2: Có trường image đơn lẻ
    if (loc.image && typeof loc.image === 'string') {
      return loc.image;
    }
    
    return null;
  };

  // Logic phân trang
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLocations = locations.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(locations.length / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getDistance = (loc) => {
    if (!userLocation.coordinates || !userLocation.coordinates.lat) return 0;
    return calculateDistance(
      userLocation.coordinates.lat,
      userLocation.coordinates.lng,
      parseFloat(loc.latitude),
      parseFloat(loc.longitude)
    ).toFixed(2);
  };

  return (
    <div className="list-page-container">
      <div className="list-header">
        <h2>🔍 Khám phá địa điểm</h2>
        <button className="back-btn" onClick={() => navigate('/')}>
          ← Xem bản đồ
        </button>
      </div>

      <div className="search-container">
        <form onSubmit={handleSearch} className="search-box">
            <input 
                type="text" 
                placeholder="Bạn đang thèm gì? (VD: Phở, Cafe...)" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button type="submit">
                <FaSearch />
            </button>
        </form>
      </div>

      {loading && <div className="loading-state">⏳ Đang xử lý...</div>}
      {error && <div className="error-state">⚠️ {error}</div>}

      {!loading && !error && locations.length === 0 && (
        <div className="empty-state">
          <p>Không tìm thấy địa điểm nào {searchTerm ? `cho từ khóa "${searchTerm}"` : 'trong bán kính 5km'}.</p>
          <button className="retry-btn" onClick={() => {
              setSearchTerm(''); 
              fetchDefaultLocations(); 
          }}>
              {searchTerm ? 'Xem tất cả' : 'Thử lại'}
          </button>
        </div>
      )}

      <div className="locations-grid">
        {currentLocations.map((loc) => {
          const imageUrl = getImageUrl(loc);
          
          return (
            <div key={loc.id} className="location-card" onClick={() => navigate(`/locations/${loc.id}`)}>
              <div className="card-image">
                {/* ✅ Logic hiển thị ảnh - ĐƠN GIẢN VÀ RÕ RÀNG */}
                {imageUrl ? (
                  <img 
                    src={imageUrl} 
                    alt={loc.name} 
                    onError={(e) => {
                      // Nếu ảnh lỗi, ẩn img và hiện placeholder
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                
                {/* Placeholder hiển thị khi không có ảnh HOẶC ảnh lỗi */}
                <div 
                  className="img-placeholder" 
                  style={{
                    ...getPlaceholderStyle(loc.name),
                    display: imageUrl ? 'none' : 'flex' // Ẩn nếu có ảnh
                  }}
                >
                  {loc.name.charAt(0).toUpperCase()}
                </div>
                
                {userLocation.loaded && !userLocation.error && (
                    <span className="distance-badge">{getDistance(loc)} km</span>
                )}
              </div>
              
              <div className="card-content">
                <h3 className="card-title">{loc.name}</h3>
                <p className="card-address"><FaMapMarkerAlt /> {loc.address}</p>
                
                <div className="card-footer">
                  <span className="card-price">
                    {loc.min_price > 0 ? `${loc.min_price.toLocaleString()}đ` : ''} 
                    {loc.max_price > 0 ? ` - ${loc.max_price.toLocaleString()}đ` : ''}
                  </span>
                  <button className="detail-btn">Xem chi tiết</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

    {/* 👇 ĐOẠN CODE PHÂN TRANG MỚI (GỌN GÀNG HƠN) */}
      {!loading && !error && locations.length > itemsPerPage && (
        <div className="pagination-controls">
          {/* Nút TRƯỚC */}
          <button 
            className="pagination-btn" 
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            &laquo; Trước
          </button>

          {/* Logic hiển thị số trang thông minh (Có dấu ...) */}
          {(() => {
            let pages = [];
            // Nếu tổng trang <= 7 thì hiện hết
            if (totalPages <= 7) {
               for (let i = 1; i <= totalPages; i++) {
                 pages.push(i);
               }
            } else {
               // Luôn hiện trang 1
               pages.push(1);

               // Nếu đang ở xa trang 1 thì hiện dấu ...
               if (currentPage > 3) {
                  pages.push('...');
               }

               // Hiện các trang xung quanh trang hiện tại
               let start = Math.max(2, currentPage - 1);
               let end = Math.min(totalPages - 1, currentPage + 1);

               // Điều chỉnh nếu ở gần đầu hoặc cuối
               if (currentPage <= 3) { end = 4; }
               if (currentPage >= totalPages - 2) { start = totalPages - 3; }

               for (let i = start; i <= end; i++) {
                  pages.push(i);
               }

               // Nếu đang ở xa trang cuối thì hiện dấu ...
               if (currentPage < totalPages - 2) {
                  pages.push('...');
               }

               // Luôn hiện trang cuối
               pages.push(totalPages);
            }

            return pages.map((page, index) => (
              <button 
                key={index} 
                onClick={() => typeof page === 'number' ? handlePageChange(page) : null}
                className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                disabled={page === '...'}
                style={page === '...' ? { border: 'none', cursor: 'default' } : {}}
              >
                {page}
              </button>
            ));
          })()}

          {/* Nút SAU */}
          <button 
            className="pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Sau &raquo;
          </button>
        </div>
      )}
    </div>
  );
}

export default LocationListPage;